import { Checkbox } from "primereact/checkbox";
import { t } from "i18next";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAssignedBuildings,
  setIsUnassignBuildingPopupOpen,
  unAssignBuilding,
  unAssignSystemServices,
  validateUnassignSystemContract,
} from "src/redux/slices/assignBuildingSlice";
import {
  getTextValueById,
  isArabic,
  isNullOrWhiteSpace,
} from "src/utils/helpers";
import { toast } from "react-toastify";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputTextarea } from "primereact/inputtextarea";
import {
  UnassignableBuildingStatuses,
  UnassignOptions,
} from "src/resources/enum";
import { Trans, useTranslation } from "react-i18next";
import { RadioButton } from "primereact/radiobutton";
interface UnassignBuildingPopupProps {
  propertyId: string;
  propertyRequestStatusId: any;
  noOfActiveContracts: number;
}

const UnassignBuildingPopup: React.FC<UnassignBuildingPopupProps> = ({
  propertyId,
  propertyRequestStatusId,
  noOfActiveContracts,
}) => {
  const dispatch = useDispatch();

  const { t } = useTranslation(["assignBuilding"]);

  const [justification, setJustification] = useState("");
  const [unassignOption, setUnassignOption] = useState(
    UnassignOptions.Building
  );

  const { isUnAssignBuildingPopupOpen, systemServicesIds } = useSelector(
    (state: any) => state.assignBuilding.unassignedBuildingDetailsPopupData
  );

  const { systems, buildingRequestStatuses } = useSelector(
    (state: any) => state.lookup
  );

  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);

  const handleCheckboxChange = (id: string) => {
    if (selectedSystems.includes(id)) {
      setSelectedSystems(selectedSystems.filter((item) => item !== id));
    } else {
      setSelectedSystems([...selectedSystems, id]);
    }
  };

  const getSystemName = (id: any) => {
    return (
      <span>
        {getTextValueById(id, systems as unknown as [], "systemServiceId")}
      </span>
    );
  };
  const isUnassignableBuilding = useMemo(() => {
    const isRejectedOrCompleted = buildingRequestStatuses.find(
      (status: { englishName: string; id: string }) =>
        (status.englishName === UnassignableBuildingStatuses.Rejected ||
          status.englishName === UnassignableBuildingStatuses.Completed) &&
        Number(status.id) === propertyRequestStatusId
    );
    return propertyRequestStatusId === null || isRejectedOrCompleted;
  }, [propertyRequestStatusId, buildingRequestStatuses]);

  const reject = () => {};
  const confirmSystem = () => {
    confirmDialog({
      message: (
        <Trans>
          <div
            dangerouslySetInnerHTML={{
              __html: t("unassignSystemConfirmation"),
            }}
          />
        </Trans>
      ),
      header: t("unassignSystem"),
      acceptLabel: t("yes"),
      rejectLabel: t("no"),
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => handleUnassignSystemServices(),
      reject,
    });
  };
  const confirmBuilding = () => {
    confirmDialog({
      message: (
        <Trans>
          <div
            dangerouslySetInnerHTML={{
              __html: t("unassignBuildingConfirmation"),
            }}
          />
        </Trans>
      ),
      header: t("unassignBuilding"),
      acceptLabel: t("yes"),
      rejectLabel: t("no"),
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => acceptUnassignBuilding(),
      reject,
    });
  };
  const unassignSelectedSystemsHandler = () => {
    dispatch(
      validateUnassignSystemContract(propertyId, selectedSystems) as any
    ).then((response: any) => {
      confirmSystem();
    });
  };
  const acceptUnassignBuilding = async () => {
    const response = await dispatch(
      unAssignBuilding(propertyId, justification, noOfActiveContracts) as any
    );
    if (response.operationSucceeded) {
      dispatch(getAssignedBuildings() as any);
      toast.success(t("buildingUnassignedSuccessfully"));
      closePopup();
    } else {
      toast.error(
        t(
          isArabic() ? response.errorDescriptionAr : response.errorDescriptionEn
        )
      );
    }
  };

  const handleBuildingUnassignment = async () => {
    confirmBuilding();
  };
  const handleUnassignSystemServices = () => {
    dispatch(unAssignSystemServices(propertyId, selectedSystems) as any).then(
      (response: any) => {
        if (
          response.data.OperationResponse.OperationSucceeded &&
          response.data.ExceededContracts.length === 0
        ) {
          toast.success(t("assetUnassignedSuccessfully"));
          dispatch(getAssignedBuildings() as any); // to refresh the grid
          closePopup();
        } else if (
          response.data?.ExceededContractsResponse?.OperationSucceeded
        ) {
          toast.error(
            isArabic()
              ? response.data.ExceededContractsResponse.ErrorDescriptionAr
              : response.data.ExceededContractsResponse.ErrorDescriptionEn
          );
        } else {
          toast.error(
            isArabic()
              ? response.data?.OperationResponse?.ErrorDescriptionAr
              : response.data.OperationResponse.ErrorDescriptionEn
          );
        }
      }
    );
  };
  const closePopup = () => {
    dispatch(setIsUnassignBuildingPopupOpen(false) as any);
  };

  const changeOption = (e: any) => {
    setSelectedSystems([]);
    setJustification("");
    setUnassignOption(e.value);
  };

  return (
    <div>
      <ConfirmDialog />
      <Dialog
        header={t("unassignBuildingPopupHeader")}
        visible={isUnAssignBuildingPopupOpen}
        onHide={closePopup}
        headerClassName="popup-header"
        className="un-assign-building-popup"
      >
        <div className="d-flex gap-2 px-2 py-3 align-items-center">
          <div className="d-flex gap-2 align-items-baseline ">
            <RadioButton
              inputId="option1"
              name="unassign-options"
              value={UnassignOptions.Building}
              onChange={changeOption}
              checked={unassignOption === UnassignOptions.Building}
            />
            <label htmlFor="option1" className="ml-2">
              {t("unassignBuilding")}
            </label>
          </div>
          <div className="d-flex gap-2 align-items-baseline">
            <RadioButton
              inputId="option2"
              name="unassign-options"
              value={UnassignOptions.System}
              onChange={changeOption}
              checked={unassignOption === UnassignOptions.System}
            />
            <label htmlFor="option2" className="ml-2">
              {t("unassignSystem")}
            </label>
          </div>
        </div>
        {unassignOption === UnassignOptions.System && (
          <div className="mt-2">
            <DataTable value={systemServicesIds.map((id: string) => ({ id }))}>
              <Column
                className="checkbox-column"
                body={(rowData: any) => (
                  <div>
                    <Checkbox
                      id={rowData.id}
                      checked={selectedSystems.includes(rowData.id)}
                      onChange={() => handleCheckboxChange(rowData.id)}
                    />
                  </div>
                )}
              />
              <Column
                field="id"
                header={t("systemName")}
                body={(rowData: any) => (
                  <label htmlFor={rowData.id}>
                    {getSystemName(rowData.id)}
                  </label>
                )}
              />
            </DataTable>
            <div className="d-flex justify-content-end px-3 mb-3 gap-2">
              <Button
                label={t("submit")}
                className="edit-btn primary-btn mx-0 my-3"
                onClick={unassignSelectedSystemsHandler}
                disabled={selectedSystems.length === 0}
              />
            </div>
          </div>
        )}
        {unassignOption === UnassignOptions.Building && (
          <section className="mb-2">
            {!isUnassignableBuilding && (
              <p className="mx-3 mb-3 mt-0 error">
                {t("unassignBuildingError")}
              </p>
            )}
            <div className="form-group px-4 mt-2">
                <label className={noOfActiveContracts > 0 ? "required" : ""}>
                {t("justification")}
                </label>
              <InputTextarea
                id="body"
                name="body"
                className="mb-2 w-100 justification"
                placeholder={t("justificationPlaceholder")}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={4}
                disabled={noOfActiveContracts === 0 || !isUnassignableBuilding}
              />
              {noOfActiveContracts === 0 && (
                <p className="note mb-3 mt-0">
                  {t("unassignBuildingJustificationError")}
                </p>
              )}
            </div>
            <div className="d-flex justify-content-end px-3 gap-2">
              <Button
                label={t("submit")}
                className="edit-btn primary-btn m-0"
                onClick={handleBuildingUnassignment}
                disabled={
                  (noOfActiveContracts > 0 &&
                    isNullOrWhiteSpace(justification)) ||
                  !isUnassignableBuilding
                }
              />
            </div>
          </section>
        )}
      </Dialog>
    </div>
  );
};

export default UnassignBuildingPopup;
